import { Button, Checkbox, Input, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

export interface KeyValueRow {
  key: string;
  value: string | null;
  description?: string | null;
  type?: string;
  isEnabled: boolean;
}

interface KeyValueRowsProps<T extends KeyValueRow> {
  rows: T[];
  onChange: (rows: T[]) => void;
  showDescription?: boolean;
  typeOptions?: { label: string; value: string }[];
  /** Invoked when "Add" is clicked (an event handler, so it's safe for this to generate an id). */
  createRow: () => T;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
}

export const KeyValueRows = <T extends KeyValueRow>({
  rows,
  onChange,
  showDescription = false,
  typeOptions,
  createRow,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
}: KeyValueRowsProps<T>) => {
  const updateRow = (index: number, patch: Partial<T>) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const addRow = () => {
    onChange([...rows, createRow()]);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((row, index) => {
        const isLast = index === rows.length - 1;
        return (
          <div key={index} className="flex items-center gap-2">
            <Checkbox
              checked={row.isEnabled}
              onChange={(event) =>
                updateRow(index, { isEnabled: event.target.checked } as Partial<T>)
              }
            />
            {typeOptions && (
              <Select
                size="small"
                className="w-24 shrink-0"
                options={typeOptions}
                value={row.type}
                onChange={(value) => updateRow(index, { type: value } as Partial<T>)}
              />
            )}
            <Input
              size="small"
              placeholder={keyPlaceholder}
              value={row.key}
              onChange={(event) => updateRow(index, { key: event.target.value } as Partial<T>)}
              className="flex-1"
            />
            <Input
              size="small"
              placeholder={valuePlaceholder}
              value={row.value ?? ''}
              onChange={(event) => updateRow(index, { value: event.target.value } as Partial<T>)}
              className="flex-1"
            />
            {showDescription && (
              <Input
                size="small"
                placeholder="Description"
                value={row.description ?? ''}
                onChange={(event) =>
                  updateRow(index, { description: event.target.value } as Partial<T>)
                }
                className="flex-1"
              />
            )}
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeRow(index)}
              aria-label="Remove row"
            />
            {isLast && (
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={addRow}
                aria-label="Add row"
              />
            )}
          </div>
        );
      })}

      {rows.length === 0 && (
        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={addRow}
          className="self-start"
          aria-label="Add row"
        />
      )}
    </div>
  );
};
